"use client";

import Image from "next/image";
import Link from "next/link";
import { Check, Phone } from "lucide-react";

import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { PENGUMUMAN_BPJS as P, ambilTeks, kodeBahasa } from "../../data/pengumuman";

/* ============================================================
   ISI HALAMAN DETAIL

   pt-[64px] md:pt-[112px] pada pembungkus paling luar DISALIN DARI
   beranda. Header situs position fixed, jadi tanpa padding ini
   kalimat pertama halaman tertutup header. Angkanya berpasangan
   dengan tinggi header - kalau tinggi header berubah, ketiga tempat
   (beranda, halaman ini, dan halaman lain yang menyalinnya) harus
   ikut berubah. Itu kelemahan yang sudah ada di kode Anda, bukan yang
   saya perkenalkan; saya hanya tidak memperburuknya dengan angka
   yang berbeda.

   URUTAN ISINYA: aturan dulu, baru caranya, baru posternya.
   Pengunjung yang mendarat di sini datang dengan satu pertanyaan -
   "saya harus apa?" - dan jawabannya ada di paragraf pertama dan
   daftar langkah. Poster ditaruh paling bawah karena fungsinya
   konfirmasi ("ya, ini pengumuman yang saya lihat di Instagram"),
   bukan sumber informasi.
   ============================================================ */

export default function ReservasiBPJSContent() {
  const ctx = useLanguage();
  const kode = kodeBahasa(ctx);

  return (
    <div className="relative w-full overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      <article className="mx-auto max-w-[68ch] px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-16">

        {/* Remah roti. Satu tingkat saja - halaman ini anak langsung
            beranda. Dibuat sebagai <nav> supaya pembaca layar bisa
            melompatinya. */}
        <nav
          aria-label={ambilTeks({ id: "Remah roti", en: "Breadcrumb" }, kode)}
          className="mb-6 text-sm text-[var(--c-body)]"
        >
          <Link
            href="/"
            className="underline underline-offset-2 hover:text-[#0f6a3b]"
          >
            {ambilTeks({ id: "Beranda", en: "Home" }, kode)}
          </Link>
          <span className="mx-2" aria-hidden="true">
            /
          </span>
          <span>{ambilTeks({ id: "Informasi", en: "Information" }, kode)}</span>
        </nav>

        <h1 className="mb-4 text-3xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] md:text-4xl">
          {ambilTeks(P.judul.sebelumMulai, kode)}
        </h1>

        {/* <time> dengan dateTime mesin-terbaca. Ini yang membedakan
            "1 Oktober" sebagai tanggal dari "1 Oktober" sebagai teks
            biasa - berguna untuk mesin pencari dan untuk pembaca layar
            yang bisa melafalkannya sebagai tanggal. */}
        <p className="mb-8 text-sm text-[var(--c-body)]">
          {ambilTeks({ id: "Berlaku mulai", en: "Effective from" }, kode)}{" "}
          <time dateTime={P.mulai.slice(0, 10)} className="font-semibold text-[var(--c-ink)]">
            {ambilTeks({ id: "1 Oktober 2026", en: "1 October 2026" }, kode)}
          </time>
        </p>

        <p className="mb-10 text-base leading-relaxed text-[var(--c-body)] md:text-lg">
          {ambilTeks(P.ringkas, kode)}
        </p>

        {/* ---------- LANGKAH ---------- */}
        <h2 className="mb-4 text-xl font-bold tracking-tight text-[var(--c-ink)] md:text-2xl">
          {ambilTeks({ id: "Cara mendaftar", en: "How to register" }, kode)}
        </h2>

        {/* <ol> bernomor, bukan kartu bernomor buatan. Isinya memang
            urutan yang harus dikerjakan berurutan, jadi penomorannya
            membawa informasi - bukan hiasan. Nomor bawaan browser juga
            otomatis dibacakan pembaca layar sebagai "1 dari 4". */}
        <ol className="mb-12 grid gap-4">
          {P.langkah.map((l, i) => (
            <li key={i} className="flex gap-4">
              <span
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#0f6a3b] text-sm font-semibold text-white"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span className="pt-0.5 text-base leading-relaxed text-[var(--c-body)]">
                {ambilTeks(l, kode)}
              </span>
            </li>
          ))}
        </ol>

        {/* ---------- KEUNTUNGAN ---------- */}
        <h2 className="mb-4 text-xl font-bold tracking-tight text-[var(--c-ink)] md:text-2xl">
          {ambilTeks(
            { id: "Kenapa lewat Aplikasi Mobile JKN", en: "Why use Mobile JKN" },
            kode
          )}
        </h2>

        <ul className="mb-12 grid gap-3">
          {P.keuntungan.map((k, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className="mt-1 h-4 w-4 shrink-0 text-[#0f6a3b]" aria-hidden="true" />
              <span className="text-base leading-relaxed text-[var(--c-body)]">
                {ambilTeks(k, kode)}
              </span>
            </li>
          ))}
        </ul>

        {/* ---------- BANTUAN ---------- */}
        {/* Nomor telepon sebagai <a href="tel:">, bukan teks biasa. Di
            HP - dari mana mayoritas pengunjung datang - itu perbedaan
            antara satu ketukan dan menyalin nomor secara manual. */}
        <div className="mb-12 rounded-2xl border border-[var(--c-line)] bg-white p-5 dark:bg-white/5 sm:p-6">
          <p className="text-base leading-relaxed text-[var(--c-body)]">
            {ambilTeks(P.bantuan, kode)}{" "}
            <a
              href={`tel:${P.telepon.tel}`}
              className="inline-flex items-center gap-1.5 font-semibold text-[#0f6a3b] underline underline-offset-2"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              {P.telepon.tampil}
            </a>
          </p>
        </div>

        {/* ---------- POSTER ---------- */}
        <h2 className="mb-4 text-xl font-bold tracking-tight text-[var(--c-ink)] md:text-2xl">
          {ambilTeks({ id: "Poster pengumuman", en: "Announcement poster" }, kode)}
        </h2>

        {/* Ukuran dibatasi 420px. Poster A4 tegak selebar kolom teks
            mendorong kaki halaman jauh ke bawah tanpa menambah apa pun
            yang belum tertulis di atas. */}
        <Image
          src={P.poster.src}
          width={P.poster.lebar}
          height={P.poster.tinggi}
          alt={ambilTeks(P.poster.alt, kode)}
          sizes="(min-width: 640px) 420px, 100vw"
          className="mx-auto w-full max-w-[420px] rounded-2xl border border-[var(--c-line)]"
        />

      </article>

      <Footer />
    </div>
  );
}
